import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { Heading } from "../components/heading";
import { Text } from "../components/text";
import { Button } from "../components/button";
import LinkFiles from "../components/LinkFiles";
import {
  ClipboardDocumentIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/solid";
import { useUser } from "../components/UserContext";
import { logBitacora } from "../utils/bitacora";

const LinkDetails = () => {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const { user, userData } = useUser();
  const [linkData, setLinkData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const uploadUrl = `${window.location.origin}/expediente/${linkId}`;

  const fetchLinkDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      // Fetch link data
      const linkDoc = await getDoc(doc(db, "links", linkId));
      if (!linkDoc.exists()) {
        setError("Link no encontrado");
        setIsLoading(false);
        return;
      }

      const linkInfo = { id: linkDoc.id, ...linkDoc.data() };

      // Fetch files from subcollection
      const filesCollectionRef = collection(db, "links", linkId, "files");
      const filesSnapshot = await getDocs(filesCollectionRef);
      const allFiles = filesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setLinkData({ ...linkInfo, allFiles });

      // Fetch client data
      const clientDoc = await getDoc(doc(db, "clientes", linkInfo.clientId));
      if (clientDoc.exists()) {
        setClientData({ id: clientDoc.id, ...clientDoc.data() });
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Error al cargar la información");
      setIsLoading(false);
    }
  }, [linkId]);
  
  const handleToggleRequired = async (fileId, isRequired) => {
    try {
      const linkRef = doc(db, "links", linkId);
      const oldRequired = linkData.files?.[fileId]?.required || false;

      await updateDoc(linkRef, {
        [`files.${fileId}.required`]: isRequired,
      });

      // Update local state directly to avoid a full page reload
      setLinkData(prevLinkData => ({
        ...prevLinkData,
        files: {
          ...prevLinkData.files,
          [fileId]: {
            ...(prevLinkData.files?.[fileId] || {}),
            required: isRequired,
          },
        },
      }));

      // Get human-readable file type name
      const fileTypeLabels = {
        actaConstitutiva: 'Acta Constitutiva',
        constanciaSituacionFiscal: 'Constancia de Situación Fiscal',
        actasAsambleas: 'Actas de Asambleas',
        poderes: 'Poderes',
        comprobanteDomicilio: 'Comprobante de Domicilio',
        identificacionOficial: 'Identificación Oficial',
        acuseReciboDeclaracion: 'Acuse de Recibo de Declaración Anual',
        declaracionEjercicio: 'Declaración del Ejercicio',
        estadosFinancierosExcel: 'Estados Financieros (Excel)',
        estadosFinancieros: 'Estados Financieros (Contador)',
        estadosFinancierosParciales: 'Estados Financieros Parciales (Contador)',
        cedulaContador: 'Cédula Profesional del Contador',
        curriculumEmpresarial: 'Currículum Empresarial',
        contrato: 'Contrato',
      };
      const fileLabel = fileTypeLabels[fileId] || fileId;

      // Log the change (non-blocking)
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'links',
          documentId: linkId,
          changes: [{
            field: `files.${fileId}.required`,
            oldValue: oldRequired,
            newValue: isRequired,
          }],
          metadata: {
            clientId: linkData.clientId,
            clientName: clientData?.nombreEmpresa || 'Sin nombre',
            linkId: linkId,
            description: `${isRequired ? 'Marcó' : 'Desmarcó'} archivo '${fileLabel}' como requerido`,
          },
          user,
          userData,
        });
      } catch (logError) {
        console.error('Failed to log file required toggle:', logError);
      }
    } catch (error) {
      console.error("Error updating required status:", error);
      alert("No se pudo actualizar el estado del requisito.");
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(uploadUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {}
  };

  useEffect(() => {
    fetchLinkDetails();
  }, [fetchLinkDetails]);

  // Determine link status dynamically based on the fileNames array
  const isCompleted =
    linkData &&
    Object.entries(linkData.files || {})
      .filter(([_, config]) => config.required)
      .every(([fileId, _]) => {
        const fileInfo = linkData.files[fileId];
        return fileInfo?.fileNames?.some(f => f.status === "procesado");
      });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !linkData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Heading level={1} className="text-red-600 mb-4">
            Error
          </Heading>
          <Text>{error || "Link no encontrado"}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4 sm:py-6 max-w-7xl">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-6 mb-6">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Button
            color="white"
            onClick={() => navigate(`/app/clientes/${linkData.clientId}`)}
            className="shrink-0"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <Heading level={1} className="mb-1 text-xl sm:text-2xl truncate">
              Expediente: {linkData.name}
            </Heading>
            <Text variant="secondary" className="truncate">
              {clientData?.nombreEmpresa}
            </Text>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                isCompleted ? "bg-green-500" : "bg-orange-500"
              }`}
            />
            <Text variant="secondary">
              {isCompleted ? "Completado" : "Pendiente"}
            </Text>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 sm:p-6 border-b">
          <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-y-4">
            <Text className="font-medium text-gray-900">
              Fecha de Creación:
            </Text>
            <Text className="break-words text-gray-900">
              {linkData.createdAt?.toDate().toLocaleDateString("es-MX", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>

            <Text className="font-medium text-gray-900">Cliente:</Text>
            <Text className="break-words text-gray-900">
              {clientData?.nombreEmpresa}
            </Text>

            <Text className="font-medium text-gray-900">Contacto:</Text>
            <div>
              <Text className="break-words text-gray-900">
                {clientData?.nombreContacto}
              </Text>
              <Text variant="secondary" className="text-sm break-words">
                {clientData?.correoContacto} • {clientData?.telefonoContacto}
              </Text>
            </div>

            <Text className="font-medium text-gray-900">URL de carga:</Text>
            <div className="relative flex items-center gap-2">
              <Text className="break-words text-gray-900">{uploadUrl}</Text>
              <button
                type="button"
                aria-label="Copiar URL"
                onClick={handleCopy}
                className="ml-2 p-1 rounded text-zinc-500 hover:text-zinc-900 transition-colors focus:outline-none"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
              </button>
              {copied && (
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-xs rounded px-2 py-1 shadow transition-opacity duration-300 z-10 pointer-events-none opacity-100">
                  Enlace copiado
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <Heading level={2} className="text-lg mb-6">
            Archivos del Expediente
          </Heading>
          <LinkFiles
            linkData={linkData}
            onToggleRequired={handleToggleRequired}
          />
        </div>
      </div>
    </div>
  );
};

export default LinkDetails;
